import { NotificationsActive, NotificationsNone } from '@mui/icons-material';
import { Badge, Box, IconButton, Popover } from '@mui/material';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { AccountNumberParams } from 'pages/managePricing/managePricingResolver';
import { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { ROUTES } from 'routing';
import { createDynamicUrl } from 'shared/hooks';
import { ProvideTableData, createTableDataContext } from 'shared/providers/provideTableData';
import { TableDataContext, UserInfo } from 'shared/types';
import { DistributorsData } from 'types/backend';

interface CustomerAlertsNotificationsProps {
    user: UserInfo | null;
}

export const alertsContext = createTableDataContext<DistributorsData>();
export function CustomerAlertsNotifications({ user }: CustomerAlertsNotificationsProps) {
    const { response } = useContext<TableDataContext<DistributorsData>>(alertsContext);
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const id = open ? 'notifications-popover' : undefined;

    return (
        <>
            {response?.data?.rows.length ? (
                <>
                    <IconButton aria-label='notifications-popover' onClick={handleClick}>
                        <Badge badgeContent={response.data.totalRows} color='secondary'>
                            <NotificationsActive sx={{ color: 'white' }} />
                        </Badge>
                    </IconButton>
                    <Popover
                        id={id}
                        open={open}
                        onClose={handleClose}
                        anchorEl={anchorEl}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'center',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'center',
                        }}
                    >
                        <Box sx={{ width: '420px', maxHeight: '70vh' }}>
                            <DataGridPremium
                                autoHeight
                                getRowId={(row) => row.customerNumber}
                                rows={[...response.data.rows].sort((a, b) =>
                                    a.customerName.localeCompare(b.customerName)
                                )}
                                columns={[
                                    {
                                        headerName: 'Customer Name',
                                        field: 'customerName',
                                        width: 280,
                                        renderCell: (params) => (
                                            <Link
                                                to={createDynamicUrl<AccountNumberParams>({
                                                    route: ROUTES.managePricing,
                                                    replaceParams: { accountNumber: params.row.customerNumber },
                                                })}
                                                onClick={handleClose}
                                            >
                                                {params.row.customerName}
                                            </Link>
                                        ),
                                    },
                                    {
                                        headerName: 'Expiring Soon',
                                        description: 'Deviations expiring within 14 days',
                                        field: 'alerts',
                                        width: 120,
                                        type: 'number',
                                    },
                                ]}
                            />
                        </Box>
                    </Popover>
                </>
            ) : (
                <NotificationsNone sx={{ color: 'white' }} />
            )}
        </>
    );
}

export function CustomerAlertsNotificationsResolver({ user }: CustomerAlertsNotificationsProps) {
    return (
        <ProvideTableData name='AlertsNotificationsSummary' tableDataContext={alertsContext}>
            <CustomerAlertsNotifications user={user} />
        </ProvideTableData>
    );
}
